import {
  ArrowLeft,
  ArrowRight,
  CalendarHeart,
  Check,
  Heart,
  LockKey,
  PencilSimple,
  Sparkle,
} from "@phosphor-icons/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { activities, atmospheres, displayName, findChoice, mains, starters, steps } from "./config/dateNight";
import { visualVariants } from "./config/visualVariants";
import { getInvitation, getLatestSubmission, submitInvitation } from "./lib/api";
import { clearDraft, loadDraft, saveDraft } from "./lib/storage";
import type { AdminSubmission, Choice, DateNightAnswers, DateNightStep, InvitationResponse } from "./types/dateNight";

const initialAnswers: DateNightAnswers = { dessert: "surprise", activities: [] };

function AmbientBackground({ warm = false }: { warm?: boolean }) {
  return (
    <div className={`ambient ${warm ? "ambient--warm" : ""}`} aria-hidden="true">
      <div className="ambient__orb ambient__orb--wine" />
      <div className="ambient__orb ambient__orb--neutral" />
      <div className="ambient__grain" />
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled, loading }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; loading?: boolean }) {
  return (
    <motion.button
      className="primary-button"
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      whileHover={disabled ? undefined : { y: -2 }}
      whileTap={disabled ? undefined : { scale: 0.985 }}
    >
      <span>{loading ? "Even opslaan..." : children}</span>
      {!loading && <ArrowRight size={19} weight="bold" aria-hidden="true" />}
      {loading && <Heart className="button-heart" size={18} weight="fill" aria-hidden="true" />}
    </motion.button>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="back-button" onClick={onClick}>
      <ArrowLeft size={16} weight="bold" aria-hidden="true" />
      Terug
    </button>
  );
}

const progressMap: Partial<Record<DateNightStep, number>> = { starter: 1, main: 2, dessert: 3, activities: 4, atmosphere: 5, summary: 5 };

function ProgressIndicator({ step }: { step: DateNightStep }) {
  const active = progressMap[step] ?? 0;
  if (!active) return null;
  return (
    <div className="progress" aria-label={`Onderdeel ${active} van 5`}>
      <span>{String(active).padStart(2, "0")}</span>
      <div className="progress__marks" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((number) => <i key={number} className={number <= active ? "is-active" : ""} />)}
      </div>
      <span>05</span>
    </div>
  );
}

function Screen({ children, stepKey }: { children: React.ReactNode; stepKey: string }) {
  const reduced = useReducedMotion();
  return (
    <motion.main
      className="screen"
      key={stepKey}
      initial={reduced ? false : { opacity: 0, y: 14, filter: "blur(5px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      exit={reduced ? undefined : { opacity: 0, y: -7, filter: "blur(3px)" }}
      transition={{ duration: reduced ? 0 : 0.36, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.main>
  );
}

function ScreenHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <header className="screen-header">
      <h1>{title}</h1>
      <p>{subtitle}</p>
    </header>
  );
}

function FoodChoiceCard({ choice, index, selected, onSelect }: { choice: Choice; index: number; selected: boolean; onSelect: () => void }) {
  const variant = visualVariants[choice.visualVariant ?? "wine"];
  return (
    <motion.button
      type="button"
      aria-pressed={selected}
      className={`food-card ${selected ? "is-selected" : ""}`}
      style={{ "--card-color": variant.color, "--card-soft": variant.soft, "--card-glow": variant.glow } as React.CSSProperties}
      onClick={onSelect}
      whileHover={{ y: -3, scale: 1.006 }}
      whileTap={{ scale: 0.985 }}
      layout
    >
      <span className="food-card__number">{String(index + 1).padStart(2, "0")}</span>
      <span className="food-card__ghost" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
      <span className="food-card__orb" aria-hidden="true" />
      <span className="food-card__content">
        <span className="food-card__title">{choice.title}</span>
        <span className="food-card__description">{choice.description}</span>
      </span>
      <span className="food-card__state">
        {selected ? <><Heart size={14} weight="fill" /> Jouw keuze</> : <ArrowRight size={17} />}
      </span>
    </motion.button>
  );
}

function FoodStep({ title, subtitle, choices, value, onChange, onBack, onContinue }: { title: string; subtitle: string; choices: Choice[]; value?: string; onChange: (id: string) => void; onBack: () => void; onContinue: () => void }) {
  return (
    <div className="content content--wide">
      <div className="topline"><BackButton onClick={onBack} /></div>
      <ScreenHeader title={title} subtitle={subtitle} />
      <div className="food-grid">
        {choices.map((choice, index) => <FoodChoiceCard key={choice.id} choice={choice} index={index} selected={value === choice.id} onSelect={() => onChange(choice.id)} />)}
      </div>
      <div className="sticky-action"><PrimaryButton onClick={onContinue} disabled={!value}>Verder</PrimaryButton></div>
    </div>
  );
}

function ActivityStep({ values, onToggle, onBack, onContinue }: { values: string[]; onToggle: (id: string) => void; onBack: () => void; onContinue: () => void }) {
  return (
    <div className="content">
      <div className="topline"><BackButton onClick={onBack} /></div>
      <ScreenHeader title="En na het eten...?" subtitle="Je mag er trouwens meer dan één kiezen 😉" />
      <div className="activity-grid">
        {activities.map((choice) => {
          const selected = values.includes(choice.id);
          return (
            <motion.button key={choice.id} type="button" aria-pressed={selected} className={`activity-card ${selected ? "is-selected" : ""}`} onClick={() => onToggle(choice.id)} whileTap={{ scale: 0.96 }}>
              <span className="activity-card__icon" aria-hidden="true">{choice.icon}</span>
              <span>{choice.title}</span>
              {selected && <motion.span className="activity-card__check" initial={{ scale: 0 }} animate={{ scale: 1 }}><Check size={13} weight="bold" /></motion.span>}
            </motion.button>
          );
        })}
      </div>
      <div className="sticky-action"><PrimaryButton onClick={onContinue} disabled={values.length === 0}>Verder</PrimaryButton></div>
    </div>
  );
}

function AtmosphereStep({ value, onChange, onBack, onContinue }: { value?: string; onChange: (id: string) => void; onBack: () => void; onContinue: () => void }) {
  return (
    <div className="content content--atmosphere">
      <div className="topline"><BackButton onClick={onBack} /></div>
      <ScreenHeader title="Nog één ding..." subtitle="Hoe wil je dat ik het gezellig maak?" />
      <div className="atmosphere-grid">
        {atmospheres.map((choice) => {
          const selected = value === choice.id;
          const variant = visualVariants[choice.visualVariant ?? "wine"];
          return (
            <motion.button key={choice.id} type="button" aria-pressed={selected} className={`atmosphere-card ${selected ? "is-selected" : ""}`} style={{ "--card-color": variant.color, "--card-soft": variant.soft, "--card-glow": variant.glow } as React.CSSProperties} onClick={() => onChange(choice.id)} whileHover={{ y: -3 }} whileTap={{ scale: 0.985 }}>
              <span className="atmosphere-card__light" aria-hidden="true" />
              <span className="atmosphere-card__icon" aria-hidden="true">{choice.icon}</span>
              <span className="atmosphere-card__title">{choice.title}</span>
              <span className="atmosphere-card__description">{choice.description}</span>
              {selected && <span className="atmosphere-card__check"><Check size={14} weight="bold" /> Jouw keuze</span>}
            </motion.button>
          );
        })}
      </div>
      <div className="sticky-action"><PrimaryButton onClick={onContinue} disabled={!value}>Verder</PrimaryButton></div>
    </div>
  );
}

function SummaryRow({ label, title, detail, onEdit, locked }: { label: string; title: string; detail?: string; onEdit?: () => void; locked?: boolean }) {
  return (
    <motion.section className="summary-row" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <span className="summary-row__label">{label}</span>
      <div>
        <h2>{title} {locked && <LockKey size={16} weight="fill" aria-label="Verrassing" />}</h2>
        {detail && <p>{detail}</p>}
      </div>
      {onEdit && <button type="button" onClick={onEdit}><PencilSimple size={14} /> Wijzigen</button>}
    </motion.section>
  );
}

function Summary({ answers, onBack, onEdit, onSubmit, submitting, error }: { answers: DateNightAnswers; onBack: () => void; onEdit: (step: DateNightStep) => void; onSubmit: () => void; submitting: boolean; error?: string }) {
  const starter = findChoice(starters, answers.starter);
  const main = findChoice(mains, answers.mainCourse);
  return (
    <div className="content content--summary">
      <div className="topline"><BackButton onClick={onBack} /></div>
      <ScreenHeader title="Perfect. ❤️" subtitle="Volgens mij weet ik genoeg." />
      <p className="summary-kicker">Jouw avond</p>
      <div className="summary-list">
        <SummaryRow label="Voorgerecht" title={starter?.title ?? ""} detail={starter?.description} onEdit={() => onEdit("starter")} />
        <SummaryRow label="Hoofdgerecht" title={main?.title ?? ""} detail={main?.description} onEdit={() => onEdit("main")} />
        <SummaryRow label="Dessert" title="Mijn verrassing" locked />
        <SummaryRow label="Na het eten" title={answers.activities.map((id) => displayName(activities, id)).join(" · ")} onEdit={() => onEdit("activities")} />
        <SummaryRow label="Sfeer" title={displayName(atmospheres, answers.atmosphere ?? "")} onEdit={() => onEdit("atmosphere")} />
      </div>
      {error && <div className="submission-error" role="alert"><strong>Oeps...</strong><span>Dat ging niet helemaal goed. Je keuzes zijn gelukkig nog bewaard.</span></div>}
      <PrimaryButton onClick={onSubmit} loading={submitting}>Dit wordt mijn avond <Heart size={17} weight="fill" aria-hidden="true" /></PrimaryButton>
    </div>
  );
}

function ExistingSubmission({ invitation, onChange, onPerfect }: { invitation: InvitationResponse; onChange: () => void; onPerfect: () => void }) {
  const answers = invitation.answers ?? initialAnswers;
  return (
    <div className="content content--summary existing">
      <CalendarHeart size={28} weight="duotone" aria-hidden="true" />
      <ScreenHeader title="Je avond staat al klaar. ❤️" subtitle="Alles wat je koos, staat nog precies zoals je het achterliet." />
      <div className="summary-list">
        <SummaryRow label="Voorgerecht" title={displayName(starters, answers.starter ?? "")} />
        <SummaryRow label="Hoofdgerecht" title={displayName(mains, answers.mainCourse ?? "")} />
        <SummaryRow label="Dessert" title="Mijn verrassing" locked />
        <SummaryRow label="Na het eten" title={answers.activities.map((id) => displayName(activities, id)).join(" · ")} />
        <SummaryRow label="Sfeer" title={displayName(atmospheres, answers.atmosphere ?? "")} />
      </div>
      {invitation.allowChanges && <PrimaryButton onClick={onChange}>Nog iets veranderen</PrimaryButton>}
      <button className="quiet-button" type="button" onClick={onPerfect}>Zo is het perfect</button>
    </div>
  );
}

function DateNightPage({ token }: { token: string }) {
  const [invitation, setInvitation] = useState<InvitationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [step, setStep] = useState<DateNightStep>("welcome");
  const [answers, setAnswers] = useState<DateNightAnswers>(initialAnswers);
  const [editing, setEditing] = useState(false);
  const [showExisting, setShowExisting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => setLoading(true), 160);
    void getInvitation(token)
      .then((data) => {
        if (!active) return;
        setInvitation(data);
        const draft = loadDraft(token);
        if (draft && !data.submitted) { setAnswers(draft.answers); setStep(draft.currentStep); }
        if (data.submitted && data.answers) { setAnswers(data.answers); setShowExisting(true); }
      })
      .catch(() => { if (active) setInvalid(true); })
      .finally(() => { if (active) { window.clearTimeout(timer); setLoading(false); } });
    return () => { active = false; window.clearTimeout(timer); };
  }, [token]);

  useEffect(() => {
    if (!loading && !invalid && !showExisting && step !== "confirmation") saveDraft(token, step, answers);
  }, [answers, invalid, loading, showExisting, step, token]);

  useEffect(() => {
    const onPopState = () => {
      const state: unknown = window.history.state;
      const historyStep = typeof state === "object" && state !== null && "dateNightStep" in state && typeof state.dateNightStep === "string"
        ? state.dateNightStep as DateNightStep
        : undefined;
      if (historyStep && steps.includes(historyStep)) setStep(historyStep);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const go = useCallback((next: DateNightStep, replace = false) => {
    setStep(next);
    const method = replace ? "replaceState" : "pushState";
    window.history[method]({ dateNightStep: next }, "", window.location.href);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const forward = useCallback((next: DateNightStep) => {
    if (editing) { setEditing(false); go("summary"); } else go(next);
  }, [editing, go]);

  const back = useCallback(() => {
    const index = steps.indexOf(step);
    if (index > 0) go(steps[index - 1]);
  }, [go, step]);

  const submit = async () => {
    setSubmitting(true); setSubmitError(undefined);
    try {
      await submitInvitation(token, answers);
      clearDraft(token);
      go("confirmation");
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Probeer het nog een keer.");
    } finally { setSubmitting(false); }
  };

  const edit = (target: DateNightStep) => { setEditing(true); go(target); };

  if (loading) return <div className="app-shell"><AmbientBackground /><main className="loading-state" aria-label="Uitnodiging wordt geladen"><Heart size={24} weight="fill" /></main></div>;
  if (invalid || !invitation?.active) return <div className="app-shell"><AmbientBackground /><Screen stepKey="invalid"><div className="center-moment"><Sparkle size={24} weight="duotone" /><h1>Deze uitnodiging is verdwenen.</h1><p>Misschien heb je niet helemaal de juiste link.</p></div></Screen></div>;
  if (showExisting) return <div className="app-shell"><AmbientBackground /><Screen stepKey="existing"><ExistingSubmission invitation={invitation} onChange={() => { setShowExisting(false); setEditing(false); go("starter", true); }} onPerfect={() => go("confirmation")} /></Screen></div>;

  return (
    <div className="app-shell">
      <AmbientBackground warm={step === "confirmation"} />
      <ProgressIndicator step={step} />
      <AnimatePresence mode="wait" initial={false}>
        {step === "welcome" && <Screen stepKey="welcome"><div className="welcome"><motion.div className="welcome__mark" initial={{ opacity: 0, scale: .7 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: .08 }}><Heart size={18} weight="fill" /></motion.div><motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .15 }}>Een avond<br />voor jou. <em>❤️</em></motion.h1><motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .27 }}>Ik regel de avond.<br />Jij hoeft alleen maar een paar belangrijke keuzes te maken.</motion.p><motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .38 }}><PrimaryButton onClick={() => go("starter")}>Beginnen</PrimaryButton><span className="welcome__note">Er zijn geen verkeerde keuzes.</span></motion.div></div></Screen>}
        {step === "starter" && <Screen stepKey="starter"><FoodStep title="Waar zullen we mee beginnen?" subtitle="Kies waar je het meeste zin in hebt." choices={starters} value={answers.starter} onChange={(starter) => setAnswers((a) => ({ ...a, starter }))} onBack={back} onContinue={() => forward("main")} /></Screen>}
        {step === "main" && <Screen stepKey="main"><FoodStep title="En als hoofdgerecht?" subtitle="Nu wordt het serieus." choices={mains} value={answers.mainCourse} onChange={(mainCourse) => setAnswers((a) => ({ ...a, mainCourse }))} onBack={back} onContinue={() => forward("dessert")} /></Screen>}
        {step === "dessert" && <Screen stepKey="dessert"><div className="content dessert"><div className="topline"><BackButton onClick={back} /></div><motion.p className="dessert__pause" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: .12 }}>Ho, wacht even...</motion.p><motion.div className="dessert__reveal" initial={{ opacity: 0, scale: .95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: .55, duration: .5 }}><h1>Het toetje heb ik al gekozen. ❤️</h1><p>Deze moet je maar aan mij overlaten.</p><div className="mystery-card"><span className="mystery-card__shimmer" /><Sparkle size={21} weight="duotone" /><strong>Mijn verrassing</strong><Heart size={17} weight="fill" /></div><PrimaryButton onClick={() => go("activities")}>Oké, ik vertrouw je</PrimaryButton></motion.div></div></Screen>}
        {step === "activities" && <Screen stepKey="activities"><ActivityStep values={answers.activities} onToggle={(id) => setAnswers((a) => ({ ...a, activities: a.activities.includes(id) ? a.activities.filter((item) => item !== id) : [...a.activities, id] }))} onBack={back} onContinue={() => forward("atmosphere")} /></Screen>}
        {step === "atmosphere" && <Screen stepKey="atmosphere"><AtmosphereStep value={answers.atmosphere} onChange={(atmosphere) => setAnswers((a) => ({ ...a, atmosphere }))} onBack={back} onContinue={() => forward("summary")} /></Screen>}
        {step === "summary" && <Screen stepKey="summary"><Summary answers={answers} onBack={back} onEdit={edit} onSubmit={() => { void submit(); }} submitting={submitting} error={submitError} /></Screen>}
        {step === "confirmation" && <Screen stepKey="confirmation"><div className="center-moment confirmation"><motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 180, damping: 15 }}><Heart size={28} weight="fill" /></motion.div><h1>It's a <span>date</span>. ❤️</h1><p>Ik regel de rest.<br />Jij hoeft alleen nog maar te komen.</p></div></Screen>}
      </AnimatePresence>
    </div>
  );
}

function AdminPage() {
  const [submission, setSubmission] = useState<AdminSubmission | null>();
  const [error, setError] = useState(false);
  useEffect(() => { void getLatestSubmission().then(setSubmission).catch(() => setError(true)); }, []);
  const date = useMemo(() => submission ? new Intl.DateTimeFormat("nl-NL", { dateStyle: "long", timeStyle: "short" }).format(new Date(submission.updatedAt ?? submission.submittedAt)) : "", [submission]);
  return (
    <div className="app-shell admin-shell"><AmbientBackground />
      <main className="admin">
        <p className="admin__brand">Date night</p>
        {submission === undefined && <div className="admin__loading"><Heart size={20} weight="fill" /> Even kijken...</div>}
        {error && <div className="admin__empty"><h1>Geen toegang.</h1><p>Controleer je Cloudflare Access-configuratie.</p></div>}
        {submission === null && <div className="admin__empty"><h1>Nog even geduld.</h1><p>Melissa heeft nog niets bevestigd.</p></div>}
        {submission && <>
          <header className="admin__header"><h1>{submission.recipientName}</h1><p>{date}</p></header>
          <div className="admin__sections">
            <SummaryRow label="Voorgerecht" title={displayName(starters, submission.starter)} />
            <SummaryRow label="Hoofdgerecht" title={displayName(mains, submission.mainCourse)} />
            <SummaryRow label="Dessert" title="Mijn verrassing" />
            <SummaryRow label="Na het eten" title={submission.activities.map((id) => displayName(activities, id)).join(" · ")} />
            <SummaryRow label="Sfeer" title={displayName(atmospheres, submission.atmosphere)} />
          </div>
        </>}
      </main>
    </div>
  );
}

export default function App() {
  const path = window.location.pathname.replace(/\/+$/, "");
  if (path === "/date/admin") return <AdminPage />;
  const match = path.match(/^\/date\/([A-Za-z0-9_-]{10,128})$/);
  return match ? <DateNightPage token={match[1]} /> : <div className="app-shell"><AmbientBackground /><Screen stepKey="missing"><div className="center-moment"><Sparkle size={24} /><h1>Deze uitnodiging is verdwenen.</h1><p>Misschien heb je niet helemaal de juiste link.</p></div></Screen></div>;
}
